import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getQueryFn, apiRequest, getCsrfToken } from "./queryClient";

interface AuthUser {
  id: number;
  email: string;
  displayName: string | null;
  role: string;
  emailVerified: boolean;
  totpEnabled: boolean;
  oauthProvider: string | null;
  hasPassword: boolean;
  requires2FASetup: boolean;
}

interface AuthContextType {
  user: AuthUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isAdmin: boolean;
  login: (email: string, password: string, totpCode?: string) => Promise<{ requires2FA?: boolean; error?: string }>;
  register: (email: string, password: string, displayName: string) => Promise<{ error?: string }>;
  logout: () => Promise<void>;
  refresh: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();

  const { data: user, isLoading, refetch } = useQuery<AuthUser | null>({
    queryKey: ["/api/auth/me"],
    queryFn: getQueryFn({ on401: "returnNull" }),
    staleTime: 5 * 60 * 1000,
    retry: (failureCount, error) => {
      if (error instanceof Error && error.message.includes("401")) return false;
      return failureCount < 2;
    },
  });

  const login = useCallback(async (email: string, password: string, totpCode?: string): Promise<{ requires2FA?: boolean; error?: string }> => {
    try {
      const csrf = getCsrfToken();
      const csrfHeaders: Record<string, string> = csrf ? { "x-csrf-token": csrf } : {};
      const res = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...csrfHeaders },
        body: JSON.stringify({ email, password, totpCode }),
        credentials: "include",
      });
      const data = await res.json();
      if (data.requires2FA) return { requires2FA: true };
      if (!res.ok) return { error: data.message || "Login failed." };
      await fetch("/api/auth/link-sessions", { method: "POST", headers: csrfHeaders, credentials: "include" }).catch((err) => console.warn("[auth] session link failed:", err));
      await refetch();
      return {};
    } catch {
      return { error: "Connection error. Please try again." };
    }
  }, [refetch]);

  const register = useCallback(async (email: string, password: string, displayName: string): Promise<{ error?: string }> => {
    try {
      const csrf = getCsrfToken();
      const csrfHeaders: Record<string, string> = csrf ? { "x-csrf-token": csrf } : {};
      const res = await fetch("/api/auth/register", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...csrfHeaders },
        body: JSON.stringify({ email, password, displayName }),
        credentials: "include",
      });
      const data = await res.json();
      if (!res.ok) return { error: data.message || "Registration failed." };
      await fetch("/api/auth/link-sessions", { method: "POST", headers: csrfHeaders, credentials: "include" }).catch((err) => console.warn("[auth] session link failed:", err));
      await refetch();
      return {};
    } catch {
      return { error: "Connection error. Please try again." };
    }
  }, [refetch]);

  const logout = useCallback(async () => {
    try {
      const csrf = getCsrfToken();
      await fetch("/api/public/logout", { method: "POST", headers: csrf ? { "x-csrf-token": csrf } : {}, credentials: "include" });
    } catch {}
    queryClient.setQueryData(["/api/auth/me"], null);
    queryClient.removeQueries({ queryKey: ["/api/auth/me"] });
    queryClient.clear();
    window.location.href = "/login";
  }, [queryClient]);

  const refresh = useCallback(() => { refetch(); }, [refetch]);

  return (
    <AuthContext.Provider value={{
      user: user ?? null,
      isLoading,
      isAuthenticated: !!user,
      isAdmin: user?.role === "admin",
      login,
      register,
      logout,
      refresh,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
